import FrameSize from "@builder/components/app/OliverHeader/FrameSize";
import FrameSizePrint from "@builder/components/app/OliverHeader/FrameSizePrint";
import ScaleInput from "@builder/components/app/OliverHeader/ScaleInput";
import Button from "@builder/components/general/Button";
import { redoBuildState, undoBuildState } from "@builder/store/slices/BuilderSlice/builderSlice";
import { setCurrentPopup } from "@builder/store/slices/popupSlice";
import { BuildTypes, GlobalBlockTypes } from "@common/build/types";
import { useEffect } from "react";
import { closeLeftSidebar, openLeftSidebar, setLeftInstance } from "../../../store/slices/sidebarSlice";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import "./style.scss";
import OliverText from "@builder/components/general/OliverText";
import { useTranslation } from "react-i18next";
import TooltipElement from "@builder/components/general/Tooltip";
import useSaveBuildSettingsKey from "@builder/hooks/useSaveBuildSettingsKey";
import { usePublishBuildMutation, useSaveBuildMutation } from "@builder/store/slices/BuildsApiSlice/buildsApiSlice";
import { toast } from "react-hot-toast";

const OliverHeader: React.FC = () => {
    const dispatch = useAppDispatch();
    const sidebars = useAppSelector(state => state.sidebar);
    // const id = useAppSelector(state => state.builder.build.id);
    const globalBlockType = useAppSelector(state => state.builder.build.globalBlockType);
    const buildType = useAppSelector(state => state.builder.build.buildType);
    const buildName = useAppSelector(state => state.builder.build.name);
    const isBuildPast = useAppSelector(state => state.builder.buildHistory.past.length);
    const isBuildFuture = useAppSelector(state => state.builder.buildHistory.future.length);
    const { t } = useTranslation();
    const { isLoading: saveBSKLoading, save: saveBuildSettingsKey } = useSaveBuildSettingsKey();
    const build = useAppSelector(state => state.builder.build);
    const [saveBuild, { isLoading: saveBuildLoading }] = useSaveBuildMutation();
    const [publishBuild, { isLoading: publishBuildLoading }] = usePublishBuildMutation();

    useEffect(() => {
        if (buildName) document.title = `Final Build - ` + buildName;
    }, [buildName]);

    //Set which frame size element
    let frameSizeElem = null;
    switch (globalBlockType) {
        case GlobalBlockTypes.GB_PRINT:
            frameSizeElem = <FrameSizePrint />;
            break;
        default:
            frameSizeElem = <FrameSize />;
    }

    const toggleAddElement = () => {
        if (sidebars.leftSidebar.instance !== "addElement") {
            dispatch(setLeftInstance("addElement"));
            if (!sidebars.leftSidebar.isOpen) {
                dispatch(openLeftSidebar());
            }
        } else {
            if (sidebars.leftSidebar.isOpen) {
                dispatch(closeLeftSidebar());
            } else {
                dispatch(openLeftSidebar());
            }
        }
    };

    const toggleAssistant = () => {
        dispatch(setLeftInstance("assistant"));
        if (!sidebars.leftSidebar.isOpen) {
            dispatch(openLeftSidebar());
        } else {
            dispatch(closeLeftSidebar());
        }
    };

    const handlePublishClick = async () => {
        if (build.companyId) {
            if (buildType === BuildTypes.OLIVER_TEMPLATE) {
                publishBuild({ companyId: build.companyId, build });
                await saveBuildSettingsKey();
            } else {
                dispatch(setCurrentPopup("publishBuild"));
            }
        }
    };

    const handleSaveBuild = async () => {
        if (build.companyId) {
            try {
                await saveBuild({ companyId: build.companyId, build }).unwrap();
                await saveBuildSettingsKey();
                toast.success(t("toast.buildSaved"));
            } catch (error) {
                console.error("error", error);
                toast.error(t("toast.buildSaveFailed"));
            }
        }
    };

    return (
        <header className="oliver-header">
            <div className="header-section">
                <div className="inner-section" onClick={toggleAssistant}></div>
                <div className="inner-section" onClick={toggleAddElement}>
                    <div className="button-container">
                        <Button
                            buttonStyle={sidebars.leftSidebar.isOpen && sidebars.leftSidebar.instance === "addElement" ? "secondary" : "primary"}
                            icon={{ src: "/plus-icon-white.svg", side: "start" }}
                            size="fit"
                        />
                        <OliverText
                            text="Add Element"
                            size="s"
                            translatePrefix="generalComponents.head."
                            color="base-blue"
                            style={{ marginInline: "12px 8px" }}
                        />
                    </div>
                </div>
                <div className="vertical-divider" />
                <TooltipElement text={t("tooltip.globalActions")} position="bottom">
                    <div className="inner-section" onClick={() => dispatch(setCurrentPopup("actions"))}>
                        <div className="button-container">
                            <Button buttonStyle="transparent" icon={{ src: "/actions-icon.svg", side: "start" }} size="fit" />
                            <OliverText text="Actions" size="s" translatePrefix="generalComponents.head." color="base-blue" />
                        </div>
                    </div>
                </TooltipElement>
            </div>
            <div className="header-section">
                <ScaleInput />
                {frameSizeElem}
                <TooltipElement text="Undo (Ctrl+Z)" translationPrefix="generalComponents.head.">
                    <button className="header-button undo" onClick={() => dispatch(undoBuildState())} disabled={!isBuildPast}>
                        <img src="/undo-icon.svg" alt="" />
                    </button>
                </TooltipElement>

                <TooltipElement text="Redo (Ctrl+Y)" translationPrefix="generalComponents.head.">
                    <button className="header-button redo" onClick={() => dispatch(redoBuildState())} disabled={!isBuildFuture}>
                        <img src="/undo-icon.svg" alt="" />
                    </button>
                </TooltipElement>
            </div>
            <div className="header-section">
                <div className="button-container-2">
                    <Button
                        buttonStyle="secondary"
                        size="small"
                        text={saveBuildLoading || publishBuildLoading || saveBSKLoading ? "" : "Save"}
                        icon={saveBuildLoading || publishBuildLoading || saveBSKLoading ? { src: "./FinalLoader.gif", filter: "white" } : undefined}
                        className="button-save-color"
                        style={{ width: "100%", height: "100%", padding: "0" }}
                        onClick={() => handleSaveBuild()}
                    />
                </div>
                <div className="button-container-2">
                    <TooltipElement text={t("tooltip.publish")} position="bottom">
                        <Button
                            buttonStyle="primary"
                            size="small"
                            text="Publish"
                            style={{ width: "100%", height: "100%", padding: "0" }}
                            onClick={handlePublishClick}
                        />
                    </TooltipElement>
                </div>
            </div>
        </header>
    );
};

export default OliverHeader;
